// The admin approval decision screen. admin/CLAUDE.md: "It's a decision
// screen, not a data screen. Surface only what changes the decision:
// below-floor lines with the salesperson's stated reason, the resulting
// margin, what this customer paid before. Everything else collapses."
//
// Always visible (top to bottom): who/what/when this quotation is for,
// below-floor lines with each line's stated reason and an individual clear
// action, margin (per line and overall), and price history per product.
// Collapsed by default: the full line-by-line table and secondary request
// details (needed_by/buyer note/subtotal breakdown) — see
// components/Collapsible.jsx.
//
// Per-line floor clearing only — admin/CLAUDE.md: "Below-floor lines are
// cleared individually, not in bulk. The approval gate is per-line." There
// is no "clear all" button anywhere on this page.

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext.jsx';
import {
  useApprovalDetail,
  useApproveQuotation,
  useClearFloorLine,
  useRejectQuotation,
  useQuotationPayment,
  useMarkQuotationPaid,
  useUpdatePaymentLetter,
  useIssueAccessLink,
  useUnsealQuotation,
} from '../hooks/useApprovalDetail.js';
import PaymentLetterForm from '../components/PaymentLetterForm.jsx';
import { useAdminRecentQuotes } from '../hooks/useAdminRecentQuotes.js';
import { useDocumentAccessLog } from '../hooks/useDocumentAccessLog.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import Collapsible from '../components/Collapsible.jsx';
import SealSection from '../components/SealSection.jsx';
import { SignatureElement } from 'shared/components/SignatureElement/index.js';
import { Spinner } from 'shared/components/Feedback/index.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import { statusLabel } from '../i18n.js';
import { formatMoney, toCents, centsToDecimalString } from 'shared/money.js';
import { formatQty } from 'shared/qty.js';
import { formatDate, formatDateTime } from '../utils/formatDate.js';
import { toDateInputValue, suggestValidUntil } from '../utils/editableTerms.js';
import styles from './ApprovalDetailPage.module.css';

// Preview-only recompute of the totals approveQuotation will actually write
// (repositories/quotationRepository.js#approve's SQL, mirrored exactly):
//   discount_amount = ROUND(subtotal * discount_percent / 100, 2)
//   vat_amount       = ROUND((subtotal - discount_amount) * vat_percent / 100, 2)
//   total            = subtotal - discount_amount + freight_amount + vat_amount
// This task's bug: the approve confirm dialog showed the totals from BEFORE
// the Discount/Freight fields were applied, so the admin confirmed an amount
// that wasn't the final one. BigInt cents throughout (shared/money.js's
// toCents/centsToDecimalString), never a raw float on money, per CLAUDE.md —
// same non-authoritative "preview" posture as money.js's own
// previewLineTotalCents (the server's approve() call is still what actually
// writes these columns; this only has to be right for the confirm dialog).
function previewApprovalTotals(quotation, discountPercentInput, freightAmountInput) {
  const subtotalCents = toCents(quotation.subtotal);
  const discountPercent = Number(discountPercentInput) || 0;
  const discountAmountCents = BigInt(Math.round((Number(subtotalCents) * discountPercent) / 100));
  const freightCents = toCents(freightAmountInput || '0');
  const vatPercent = Number(quotation.vat_percent) || 0;
  const vatBaseCents = subtotalCents - discountAmountCents;
  const vatAmountCents = BigInt(Math.round((Number(vatBaseCents) * vatPercent) / 100));
  const totalCents = vatBaseCents + freightCents + vatAmountCents;

  return {
    subtotal: centsToDecimalString(subtotalCents),
    discountAmount: centsToDecimalString(discountAmountCents),
    freight: centsToDecimalString(freightCents),
    vat: centsToDecimalString(vatAmountCents),
    total: centsToDecimalString(totalCents),
  };
}

function ProductHistoryLine({ description, productId, quotationId }) {
  const { t, lang } = useLanguage();
  const { data: quotes, isLoading } = useAdminRecentQuotes(productId, quotationId);

  return (
    <div className={styles.historyLine}>
      <span className={styles.historyDescription}>{description}</span>
      {isLoading && <span className={styles.muted}>{t('state.loading')}</span>}
      {!isLoading && (!quotes || quotes.length === 0) && (
        <span className={styles.muted}>{t('workspace.recentQuotes.emptyShort')}</span>
      )}
      {!isLoading && quotes && quotes.length > 0 && (
        <span
          className="tabularNums"
          title={quotes
            .map((q) =>
              t('workspace.recentQuotes.row', {
                price: formatMoney(q.unit_price),
                organization: q.organization_name,
                date: formatDateTime(q.quoted_at, lang) || '—',
              })
            )
            .join(' | ')}
        >
          {t('workspace.recentQuotes.labelShort')}{' '}
          {quotes
            .map((q) =>
              t('workspace.recentQuotes.compactItem', {
                price: formatMoney(q.unit_price),
                date: formatDateTime(q.quoted_at, lang) || '—',
              })
            )
            .join(' · ')}
        </span>
      )}
    </div>
  );
}

// Security-hardening task, item 6: "surface it in the admin request view —
// this is both dispute evidence and sales intelligence." Local component,
// same pattern as ProductHistoryLine above — reads its own data via a hook,
// rather than the parent fetching it and passing it down, since this
// section is independent of the quotation-approval data the rest of the
// page needs.
function DocumentAccessLogSection({ requestId }) {
  const { t, lang } = useLanguage();
  const { data: entries, isLoading } = useDocumentAccessLog(requestId);

  if (isLoading) {
    return <p className={styles.muted}>{t('state.loading')}</p>;
  }
  if (!entries || entries.length === 0) {
    return <p className={styles.muted}>{t('approvalDetail.documentAccessLog.empty')}</p>;
  }

  return (
    <ul className={styles.belowFloorList}>
      {entries.map((entry) => (
        <li key={entry.id} className={styles.belowFloorItem}>
          <div className={styles.belowFloorTopRow}>
            <span className={styles.lineDescription}>
              {t(`approvalDetail.documentAccessLog.documentType.${entry.document_type}`)}
            </span>
            <span className="tabularNums">{formatDateTime(entry.accessed_at, lang) || '—'}</span>
          </div>
          <p className={styles.discountReason}>
            {t(`approvalDetail.documentAccessLog.accessorType.${entry.accessor_type}`, {
              name: entry.accessor_name || '',
            })}
            {entry.ip_address ? ` · ${entry.ip_address}` : ''}
          </p>
        </li>
      ))}
    </ul>
  );
}

// --- Payment (mark-paid): only rendered once the customer has accepted and
// a payment_requests row exists. A 404 from the payment endpoint is the
// normal "not accepted yet" state (useQuotationPayment swallows it to null)
// — this section then renders nothing. Marking paid is a financial record:
// it goes behind the shared confirm dialog, admin-only (the whole
// /quotations/:id route is admin-gated already).
function PaymentSection({ quotationId }) {
  const { t, lang, toast, confirm, toastOk, toastErr } = useActionFeedback();
  const { data: payment, isLoading } = useQuotationPayment(quotationId);
  const markPaidMutation = useMarkQuotationPaid(quotationId);
  const letterMutation = useUpdatePaymentLetter(quotationId);
  const [bankReference, setBankReference] = useState('');
  const [paidAt, setPaidAt] = useState('');

  if (isLoading || !payment) return null;

  const isPaid = payment.status === 'paid';

  const handleMarkPaid = async () => {
    if (!bankReference.trim()) {
      toast.error(t('approvalDetail.payment.bankReferenceRequired'));
      return;
    }
    const ok = await confirm({
      title: t('approvalDetail.payment.confirmTitle'),
      message: t('approvalDetail.payment.confirmBody', {
        amount: formatMoney(payment.amount_due),
      }),
      confirmLabel: t('approvalDetail.payment.markPaid'),
      tone: 'danger',
    });
    if (!ok) return;

    markPaidMutation.mutate(
      { bankReference: bankReference.trim(), paidAt: paidAt || undefined },
      {
        onSuccess: () => {
          setBankReference('');
          setPaidAt('');
          toastOk(t('approvalDetail.payment.markedPaidToast'));
        },
        onError: (err) =>
          toastErr(err, ['PAYMENT_REQUEST_NOT_FOUND', 'PAYMENT_REQUEST_ALREADY_PAID']),
      }
    );
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{t('approvalDetail.payment.title')}</h2>
      <p className={styles.paymentMeta}>
        <strong>{t('approvalDetail.payment.amountDue')}:</strong>{' '}
        <span className="tabularNums">{formatMoney(payment.amount_due)}</span>
      </p>
      <p className={styles.paymentMeta}>
        <strong>{t('approvalDetail.payment.status')}:</strong>{' '}
        {isPaid
          ? t('approvalDetail.payment.statusPaid')
          : t('approvalDetail.payment.statusAwaiting')}
      </p>

      {isPaid ? (
        <p className={styles.clearedNote}>
          {t('approvalDetail.payment.paidNote', {
            reference: payment.bank_reference || '—',
            amount: payment.paid_amount ? formatMoney(payment.paid_amount) : '—',
            date: formatDateTime(payment.paid_at, lang) || '—',
          })}
        </p>
      ) : (
        <div className={styles.paymentForm}>
          <label className={styles.approveField}>
            {t('approvalDetail.payment.bankReferenceLabel')}
            <input
              type="text"
              className={styles.paymentInput}
              maxLength={120}
              value={bankReference}
              onChange={(e) => setBankReference(e.target.value)}
            />
          </label>
          <label className={styles.approveField}>
            {t('approvalDetail.payment.paidAtLabel')}
            <input
              type="date"
              className={`${styles.paymentInput} tabularNums`}
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
            />
          </label>
          <button
            type="button"
            className={styles.approveButton}
            onClick={handleMarkPaid}
            disabled={markPaidMutation.isPending}
          >
            {markPaidMutation.isPending && <Spinner />}{' '}
            {markPaidMutation.isPending
              ? t('approvalDetail.payment.marking')
              : t('approvalDetail.payment.markPaid')}
          </button>
        </div>
      )}

      <PaymentLetterForm
        payment={payment}
        isSaving={letterMutation.isPending}
        readOnlyPaid={isPaid}
        onSave={(letterBody) =>
          letterMutation.mutate(letterBody, {
            onSuccess: () => toastOk(t('paymentLetter.savedToast')),
            onError: (err) =>
              toastErr(err, ['PAYMENT_REQUEST_NOT_FOUND', 'PAYMENT_REQUEST_LOCKED']),
          })
        }
      />
    </section>
  );
}

// --- Issue access link: re-mints a shareable guest tracking link for a
// customer who lost theirs. Only valid once the quotation has been sent at
// least once (server gate: 'sent' | 'accepted' | 'expired' | 'superseded').
// The raw token is only ever returned by this one call — surface it to copy
// immediately.
function AccessLinkSection({ quotationId, status }) {
  const { t, toastOk, toastErr } = useActionFeedback();
  const issueMutation = useIssueAccessLink(quotationId);
  const [issued, setIssued] = useState(null);

  if (!['sent', 'accepted', 'expired', 'superseded'].includes(status)) return null;

  const handleIssue = () => {
    issueMutation.mutate(undefined, {
      onSuccess: (data) => {
        setIssued(data);
        toastOk(t('approvalDetail.accessLink.issuedToast'));
      },
      onError: (err) => toastErr(err, ['QUOTATION_NOT_SENT', 'QUOTATION_NOT_FOUND']),
    });
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{t('approvalDetail.accessLink.title')}</h2>
      <p className={styles.muted}>{t('approvalDetail.accessLink.hint')}</p>
      {issued && (
        <p className={styles.accessLinkUrl}>
          <a href={issued.url} target="_blank" rel="noreferrer">
            {issued.url}
          </a>
        </p>
      )}
      <button
        type="button"
        className={styles.rejectButton}
        onClick={handleIssue}
        disabled={issueMutation.isPending}
      >
        {issueMutation.isPending && <Spinner />}{' '}
        {issueMutation.isPending
          ? t('approvalDetail.accessLink.issuing')
          : issued
            ? t('approvalDetail.accessLink.reissue')
            : t('approvalDetail.accessLink.issue')}
      </button>
    </section>
  );
}

// --- Buyer review: rendered only for a revision the BUYER triggered by
// reviewing the proforma line by line (getForApproval sets
// isBuyerReviewRevision + attaches supersededLines with the buyer's per-line
// decision). Shows old qty beside new, the declined lines with the buyer's
// stated reason, and — when the quotation is still pending — a per-line
// unit-price edit that feeds approveBodySchema.lineEdits. A real price move
// forces admin-only approval for the revision (revision_price_changed).
function BuyerReviewSection({ quotation, priceEdits, setPriceEdits, editable }) {
  const { t } = useLanguage();

  const supersededLines = quotation.supersededLines || [];
  const supersededById = new Map(supersededLines.map((l) => [l.id, l]));

  let accepted = 0;
  let changed = 0;
  let declined = 0;
  for (const l of supersededLines) {
    if (l.buyer_decision === 'declined') declined += 1;
    else if (l.buyer_decision === 'qty_changed') changed += 1;
    else accepted += 1;
  }

  const declinedLines = supersededLines.filter((l) => l.buyer_decision === 'declined');

  const setEdit = (lineId, value) => setPriceEdits((prev) => ({ ...prev, [lineId]: value }));

  return (
    <section className={styles.buyerReviewSection}>
      <h2 className={styles.sectionTitle}>{t('approvalDetail.buyerReview.title')}</h2>
      <p className={styles.buyerReviewIntro}>
        {t('approvalDetail.buyerReview.intro', { revision: quotation.revision })}
      </p>
      <p className={styles.buyerReviewSummary}>
        {t('approvalDetail.buyerReview.summary', { accepted, changed, declined })}
      </p>

      <table className={styles.allLinesTable}>
        <thead>
          <tr>
            <th>{t('approvalDetail.buyerReview.col.line')}</th>
            <th>{t('approvalDetail.buyerReview.col.decision')}</th>
            <th>{t('approvalDetail.buyerReview.col.qty')}</th>
            <th>{t('approvalDetail.buyerReview.col.unitPrice')}</th>
          </tr>
        </thead>
        <tbody>
          {quotation.lines.map((line) => {
            const orig = supersededById.get(line.supersedes_line_id);
            const qtyChanged = orig ? orig.buyer_decision === 'qty_changed' : false;
            const editVal =
              priceEdits[line.id] !== undefined ? priceEdits[line.id] : String(line.unit_price);
            const moved = editVal !== '' && Number(editVal) !== Number(line.unit_price);
            return (
              <tr key={line.id}>
                <td>{line.description}</td>
                <td>
                  <span
                    className={`${styles.decisionBadge} ${qtyChanged ? styles.decisionChanged : ''}`}
                  >
                    {t(
                      `approvalDetail.buyerReview.decision.${qtyChanged ? 'qty_changed' : 'accepted'}`
                    )}
                  </span>
                </td>
                <td className="tabularNums">
                  {qtyChanged && orig
                    ? t('approvalDetail.buyerReview.qtyChanged', {
                        was: formatQty(orig.qty),
                        now: formatQty(line.qty),
                      })
                    : t('approvalDetail.buyerReview.qtyUnchanged', { qty: formatQty(line.qty) })}
                </td>
                <td>
                  {editable ? (
                    <>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        className={`${styles.priceEditInput} ${
                          moved ? styles.priceEditInputMoved : ''
                        } tabularNums`}
                        value={editVal}
                        onChange={(e) => setEdit(line.id, e.target.value)}
                      />
                      {moved && (
                        <span className={styles.priceMovedTag}>
                          {t('approvalDetail.buyerReview.priceChanged')}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="tabularNums">{formatMoney(line.unit_price)}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {editable && (
        <p className={styles.buyerReviewHint}>{t('approvalDetail.buyerReview.priceEditHint')}</p>
      )}

      {declinedLines.length > 0 && (
        <>
          <h3 className={styles.buyerReviewDeclinedTitle}>
            {t('approvalDetail.buyerReview.declinedTitle')}
          </h3>
          <ul className={styles.belowFloorList}>
            {declinedLines.map((l) => (
              <li key={l.id} className={styles.belowFloorItem}>
                <div className={styles.belowFloorTopRow}>
                  <span className={styles.lineDescription}>{l.description}</span>
                  <span className={`${styles.decisionBadge} ${styles.decisionDeclined}`}>
                    {t(`declineReason.${l.buyer_decline_category || 'other'}`)}
                  </span>
                </div>
                {l.buyer_decline_note && (
                  <p className={styles.discountReason}>
                    {t('approvalDetail.buyerReview.declinedNote', { note: l.buyer_decline_note })}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

export default function ApprovalDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();

  const { data: quotation, isLoading, isError, error, refetch } = useApprovalDetail(id);
  const clearFloorMutation = useClearFloorLine(id);
  const approveMutation = useApproveQuotation(id);
  const rejectMutation = useRejectQuotation(id);
  // `id` is the page's own useParams() STRING — the same value
  // useApprovalDetail(id) above keys its cache entry on. Passed into the
  // shared SealSection as the injected mutation (see that component's own
  // header comment for why it never creates this hook itself) — using
  // quotation.id (a NUMBER) here instead would build a cache key that looks
  // equal but hashes differently, a real bug this codebase already hit once.
  const unsealMutation = useUnsealQuotation(id);
  const { toast, confirm, toastOk, toastErr } = useActionFeedback();

  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  // Admin approval-screen discount/freight inputs (this task's brief).
  // Plain strings, same controlled-input shape as every number field in
  // SettingsPage.jsx — parsed to a number only at submit time in
  // handleApprove below.
  const [discountPercent, setDiscountPercent] = useState('0');
  const [freightAmount, setFreightAmount] = useState('0');
  // Per-line unit-price overrides for a buyer-review revision (this task's
  // brief). Keyed by quotation_line id; a string, like the fields above.
  // Only sent for lines whose value actually differs from the current price.
  const [priceEdits, setPriceEdits] = useState({});

  // Editable-terms task: admin approval-screen OVERRIDES on top of whatever
  // sales already saved while pricing — same optional-override shape as
  // discountPercent/freightAmount above, not a mandatory re-entry. Prefilled
  // once the quotation loads: from the quotation's own saved values if it
  // has any, else from `quotation.defaults` (the same settings-derived
  // prefill getWorkspace exposes to the sales side). `termsInitializedFor`
  // guards against re-stomping an admin's in-progress edit on an unrelated
  // re-render/refetch, same pattern WorkspacePage.jsx's `initializedFor`
  // already uses for the identical reason.
  const [approveValidUntil, setApproveValidUntil] = useState('');
  const [approvePaymentTerms, setApprovePaymentTerms] = useState('');
  const [approveDeliveryDays, setApproveDeliveryDays] = useState('');
  const [termsInitializedFor, setTermsInitializedFor] = useState(null);

  useEffect(() => {
    if (!quotation || termsInitializedFor === quotation.id) return;
    const defaults = quotation.defaults || {};
    const hasDeliveryDays =
      quotation.delivery_days !== null && quotation.delivery_days !== undefined;
    setApproveValidUntil(
      quotation.valid_until
        ? toDateInputValue(quotation.valid_until)
        : suggestValidUntil(defaults.quoteValidityDays)
    );
    setApprovePaymentTerms(quotation.payment_terms || defaults.paymentTerms || '');
    setApproveDeliveryDays(
      hasDeliveryDays
        ? String(quotation.delivery_days)
        : defaults.deliveryDays !== undefined
          ? String(defaults.deliveryDays)
          : ''
    );
    setTermsInitializedFor(quotation.id);
  }, [quotation, termsInitializedFor]);

  if (isLoading) {
    return <LoadingState variant="page" />;
  }
  if (isError) {
    return <ErrorState messageId="approvalDetail.errorBody" error={error} onRetry={refetch} />;
  }

  const belowFloorLines = quotation.lines.filter((line) => line.below_floor);
  const unclearedLines = belowFloorLines.filter((line) => !line.floor_cleared_at);
  const isPending = quotation.status === 'pending_approval';
  const canApprove = isPending && unclearedLines.length === 0;
  const productLines = quotation.lines.filter((line) => line.product_id);

  const signatureStatus =
    quotation.status === 'approved' || quotation.status === 'sent' ? 'approved' : 'unallocated';

  const handleClearFloor = (lineId) => {
    clearFloorMutation.mutate(lineId, {
      onSuccess: () => toastOk(t('approvalDetail.belowFloor.clearedToast')),
      onError: (err) => toastErr(err, ['LINE_NOT_BELOW_FLOOR', 'QUOTATION_NOT_PENDING_APPROVAL']),
    });
  };

  const handleApprove = async () => {
    // Irreversible + financial: allocates a permanent proforma number and
    // queues the signed PDF. Confirm, stating the consequence AND the
    // actual resulting totals — not the pre-discount/freight figures
    // (this task's bug: the dialog previously showed stale totals).
    const currency = quotation.currency || 'ETB';
    const preview = previewApprovalTotals(quotation, discountPercent, freightAmount);
    const discountPercentNum = Number(discountPercent) || 0;
    const vatPercentNum = Number(quotation.vat_percent) || 0;

    const messageLines = [
      t('approvalDetail.confirmApprove.body'),
      '',
      t('approvalDetail.confirmApprove.lineSubtotal', {
        amount: formatMoney(preview.subtotal),
        currency,
      }),
    ];
    if (discountPercentNum > 0) {
      messageLines.push(
        t('approvalDetail.confirmApprove.lineDiscount', {
          percent: discountPercentNum,
          amount: formatMoney(preview.discountAmount),
          currency,
        })
      );
    }
    if (Number(freightAmount) > 0) {
      messageLines.push(
        t('approvalDetail.confirmApprove.lineFreight', {
          amount: formatMoney(preview.freight),
          currency,
        })
      );
    }
    messageLines.push(
      t('approvalDetail.confirmApprove.lineVat', {
        percent: vatPercentNum,
        amount: formatMoney(preview.vat),
        currency,
      }),
      t('approvalDetail.confirmApprove.lineTotal', {
        amount: formatMoney(preview.total),
        currency,
      })
    );

    const ok = await confirm({
      title: t('approvalDetail.confirmApprove.title'),
      message: messageLines.join('\n'),
      confirmLabel: t('approvalDetail.actions.approve'),
      tone: 'danger',
    });
    if (!ok) return;

    const lineEdits = Object.entries(priceEdits)
      .map(([lineId, value]) => ({ lineId: Number(lineId), unitPrice: Number(value) }))
      .filter((edit) => {
        if (!Number.isFinite(edit.unitPrice) || edit.unitPrice < 0) return false;
        const line = quotation.lines.find((l) => l.id === edit.lineId);
        return line && edit.unitPrice !== Number(line.unit_price);
      });

    approveMutation.mutate(
      {
        discountPercent: Number(discountPercent) || 0,
        freightAmount: Number(freightAmount) || 0,
        ...(lineEdits.length > 0 ? { lineEdits } : {}),
        // Editable-terms task: always sent as whatever the (prefilled,
        // editable) box currently holds — the box is already seeded from
        // the right source (sales's saved value, or the settings default)
        // by the effect above, so "send what you see" is correct without
        // needing a separate "did the admin actually touch this" check.
        validUntil: approveValidUntil || null,
        paymentTerms: approvePaymentTerms.trim() !== '' ? approvePaymentTerms.trim() : null,
        deliveryDays: approveDeliveryDays !== '' ? Number(approveDeliveryDays) : null,
      },
      {
        onSuccess: (data) =>
          toastOk(
            t('approvalDetail.approvedToast', { proformaNo: data?.quotation?.proforma_no || '' })
          ),
        onError: (err) =>
          toastErr(err, [
            'UNCLEARED_FLOOR_LINES',
            'QUOTATION_NOT_PENDING_APPROVAL',
            'BELOW_FLOOR_EDIT_REJECTED',
            'QUOTATION_LINE_NOT_FOUND',
          ]),
      }
    );
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      toast.error(t('error.EMPTY_REJECTION_REASON'));
      return;
    }
    const ok = await confirm({
      title: t('approvalDetail.confirmReject.title'),
      message: t('approvalDetail.confirmReject.body'),
      confirmLabel: t('approvalDetail.actions.rejectConfirm'),
      tone: 'danger',
    });
    if (!ok) return;

    rejectMutation.mutate(rejectReason.trim(), {
      onSuccess: () => {
        setRejecting(false);
        setRejectReason('');
        toastOk(t('approvalDetail.rejectedToast'));
      },
      onError: (err) => toastErr(err, ['EMPTY_REJECTION_REASON', 'QUOTATION_NOT_PENDING_APPROVAL']),
    });
  };

  return (
    <div className={styles.page}>
      <button type="button" className={styles.backLink} onClick={() => navigate('/')}>
        {t('approvalDetail.backToQueue')}
      </button>

      <div className={styles.header}>
        <SignatureElement proformaNo={quotation.proforma_no} status={signatureStatus} lang={lang} />
        <div className={styles.headerMeta}>
          <p>
            <strong>{t('approvalDetail.request')}:</strong>{' '}
            <span className="tabularNums">{quotation.request_ref_no}</span>
          </p>
          <p>
            <strong>{t('approvalDetail.organization')}:</strong>{' '}
            {lang === 'am' && quotation.organization_name_am
              ? quotation.organization_name_am
              : quotation.organization_name}
          </p>
          <p>
            <strong>{t('approvalDetail.pricedBy')}:</strong>{' '}
            {quotation.priced_by_name || t('approvalDetail.pricedByUnknown')}
          </p>
          <p>
            <strong>{t('approvalDetail.submittedAt')}:</strong>{' '}
            {formatDateTime(quotation.submitted_at, lang) || '—'}
          </p>
          <p>
            <strong>{t('queue.col.status')}:</strong> {statusLabel(quotation.status, lang)}
          </p>
        </div>
      </div>

      {quotation.status === 'approved' && (
        <p className={styles.decisionBanner}>
          {t('approvalDetail.decisionMade.approved', { proformaNo: quotation.proforma_no })}
        </p>
      )}
      {quotation.status === 'rejected' && (
        <p className={styles.decisionBanner}>
          {t('approvalDetail.decisionMade.rejected', { reason: quotation.rejection_reason })}
        </p>
      )}

      <hr className="hairline" />

      {/* --- Buyer review: only for a buyer-triggered revision. Old vs new
          qty, declined lines with reasons, optional per-line price edits. */}
      {quotation.isBuyerReviewRevision && (
        <BuyerReviewSection
          quotation={quotation}
          priceEdits={priceEdits}
          setPriceEdits={setPriceEdits}
          editable={isPending}
        />
      )}

      {/* --- Below-floor lines: always visible, the core decision surface. */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('approvalDetail.belowFloor.title')}</h2>
        {belowFloorLines.length === 0 ? (
          <p className={styles.muted}>{t('approvalDetail.belowFloor.none')}</p>
        ) : (
          <ul className={styles.belowFloorList}>
            {belowFloorLines.map((line) => (
              <li
                key={line.id}
                className={`${styles.belowFloorItem} ${line.floor_cleared_at ? '' : styles.belowFloorItemUncleared}`}
              >
                <div className={styles.belowFloorTopRow}>
                  <span className={styles.lineDescription}>{line.description}</span>
                  <span className="tabularNums">
                    {t('approvalDetail.belowFloor.priceVsFloor', {
                      price: formatMoney(line.unit_price),
                      floor: line.current_floor_price ? formatMoney(line.current_floor_price) : '—',
                    })}
                  </span>
                </div>
                <p className={styles.discountReason}>
                  <strong>{t('approvalDetail.belowFloor.reason')}</strong> {line.discount_reason}
                </p>
                {line.floor_cleared_at ? (
                  <p className={styles.clearedNote}>
                    {t('approvalDetail.belowFloor.cleared', {
                      name:
                        line.floor_cleared_by_name || t('approvalDetail.belowFloor.clearedUnknown'),
                      date: formatDateTime(line.floor_cleared_at, lang) || '—',
                    })}
                  </p>
                ) : (
                  <button
                    type="button"
                    className={styles.clearButton}
                    onClick={() => handleClearFloor(line.id)}
                    disabled={clearFloorMutation.isPending || !isPending}
                  >
                    {clearFloorMutation.isPending && <Spinner />}{' '}
                    {clearFloorMutation.isPending
                      ? t('approvalDetail.belowFloor.clearing')
                      : t('approvalDetail.belowFloor.clear')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* --- Margin: always visible, admin-only, never in a sales response
          (see services/quotationService.js#getForApproval's own comment). */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('approvalDetail.margin.title')}</h2>
        <p className={styles.overallMargin}>
          {t('approvalDetail.margin.overall')}:{' '}
          <span className="tabularNums">
            {quotation.preview_overall_margin_percent !== null &&
            quotation.preview_overall_margin_percent !== undefined
              ? `${quotation.preview_overall_margin_percent}%`
              : t('approvalDetail.margin.notAvailable')}
          </span>
        </p>
        {(quotation.preview_overall_margin_percent === null ||
          quotation.preview_overall_margin_percent === undefined) && (
          <p className={styles.muted}>{t('approvalDetail.margin.notAvailableHint')}</p>
        )}
        <table className={styles.marginTable}>
          <tbody>
            {quotation.lines.map((line) => (
              <tr key={line.id}>
                <td className={styles.marginLineDescription}>{line.description}</td>
                <td className="tabularNums">
                  {line.preview_margin_percent !== null && line.preview_margin_percent !== undefined
                    ? `${line.preview_margin_percent}%`
                    : t('approvalDetail.margin.notAvailable')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* --- Price history: always visible, "what this customer paid before". */}
      {productLines.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('approvalDetail.history.title')}</h2>
          {productLines.map((line) => (
            <ProductHistoryLine
              key={line.id}
              description={line.description}
              productId={line.product_id}
              quotationId={quotation.id}
            />
          ))}
        </section>
      )}

      {/* --- Sealed-bid tender: always visible when this quotation is
          sealed, not tucked behind a Collapsible — seal state is
          decision-relevant for an admin reviewing/approving a tender
          (migration 020). */}
      {Boolean(quotation.sealed) && (
        <SealSection
          quotation={quotation}
          requestId={quotation.request_id}
          unsealMutation={unsealMutation}
        />
      )}

      {/* --- Document access log: collapsed by default, dispute
          evidence/sales intelligence, not part of the pricing decision
          itself (security-hardening task, item 6). */}
      <Collapsible
        showLabelId="approvalDetail.documentAccessLog.toggleShow"
        hideLabelId="approvalDetail.documentAccessLog.toggleHide"
      >
        <DocumentAccessLogSection requestId={quotation.request_id} />
      </Collapsible>

      {/* --- Everything else: collapsed by default. */}
      <Collapsible
        showLabelId="approvalDetail.allLines.toggleShow"
        hideLabelId="approvalDetail.allLines.toggleHide"
        showLabelVars={{ count: quotation.lines.length }}
      >
        <table className={styles.allLinesTable}>
          <thead>
            <tr>
              <th>{t('approvalDetail.allLines.col.line')}</th>
              <th>{t('approvalDetail.allLines.col.description')}</th>
              <th>{t('approvalDetail.allLines.col.qty')}</th>
              <th>{t('approvalDetail.allLines.col.unitPrice')}</th>
              <th>{t('approvalDetail.allLines.col.lineTotal')}</th>
              <th>{t('approvalDetail.allLines.col.status')}</th>
            </tr>
          </thead>
          <tbody>
            {quotation.lines.map((line) => (
              <tr key={line.id}>
                <td className="tabularNums">{line.line_no}</td>
                <td>{line.description}</td>
                <td className="tabularNums">{formatQty(line.qty)}</td>
                <td className="tabularNums">{formatMoney(line.unit_price)}</td>
                <td className="tabularNums">{formatMoney(line.line_total)}</td>
                <td>
                  {!line.below_floor
                    ? t('approvalDetail.allLines.statusOk')
                    : line.floor_cleared_at
                      ? t('approvalDetail.allLines.statusBelowFloorCleared')
                      : t('approvalDetail.allLines.statusBelowFloorUncleared')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Collapsible>

      <Collapsible
        showLabelId="approvalDetail.moreDetails.toggleShow"
        hideLabelId="approvalDetail.moreDetails.toggleHide"
      >
        <div className={styles.moreDetails}>
          {quotation.request_needed_by && (
            <p>
              <strong>{t('approvalDetail.moreDetails.neededBy')}:</strong>{' '}
              {formatDate(quotation.request_needed_by, lang) || quotation.request_needed_by}
            </p>
          )}
          {quotation.request_purpose && (
            <p>
              <strong>{t('approvalDetail.moreDetails.purpose')}:</strong>{' '}
              {quotation.request_purpose}
            </p>
          )}
          {quotation.request_buyer_note && (
            <p>
              <strong>{t('approvalDetail.moreDetails.buyerNote')}:</strong>{' '}
              {quotation.request_buyer_note}
            </p>
          )}
          <p className="tabularNums">
            <strong>{t('approvalDetail.moreDetails.subtotal')}:</strong>{' '}
            {formatMoney(quotation.subtotal)}
          </p>
          {Number(quotation.discount_amount) > 0 && (
            <p className="tabularNums">
              <strong>{t('approvalDetail.moreDetails.discount')}:</strong> −
              {formatMoney(quotation.discount_amount)}
            </p>
          )}
          {Number(quotation.freight_amount) > 0 && (
            <p className="tabularNums">
              <strong>{t('approvalDetail.moreDetails.freight')}:</strong>{' '}
              {formatMoney(quotation.freight_amount)}
            </p>
          )}
          <p className="tabularNums">
            <strong>{t('approvalDetail.moreDetails.vat')}:</strong>{' '}
            {formatMoney(quotation.vat_amount)}
          </p>
          <p className="tabularNums">
            <strong>{t('approvalDetail.moreDetails.total')}:</strong> {formatMoney(quotation.total)}
          </p>
        </div>
      </Collapsible>

      {/* --- Payment + access link: self-gating on quotation/payment state.
          Both admin-only actions (this route is admin-gated). */}
      <PaymentSection quotationId={quotation.id} />
      <AccessLinkSection quotationId={quotation.id} status={quotation.status} />

      <hr className="hairline" />

      {/* --- Actions: two primary decisions, per-line floor gate enforced
          both here (disabled state) and, authoritatively, server-side. */}
      {isPending && (
        <div className={styles.actionsRow}>
          {!rejecting ? (
            <>
              <div className={styles.approveFields}>
                <label className={styles.approveField}>
                  {t('approvalDetail.actions.discountPercentLabel')}
                  <input
                    type="number"
                    className={`${styles.approveFieldInput} tabularNums`}
                    min={0}
                    max={100}
                    step="0.01"
                    value={discountPercent}
                    onChange={(e) => setDiscountPercent(e.target.value)}
                    disabled={!canApprove}
                  />
                </label>
                <label className={styles.approveField}>
                  {t('approvalDetail.actions.freightAmountLabel', {
                    currency: quotation.currency || 'ETB',
                  })}
                  <input
                    type="number"
                    className={`${styles.approveFieldInput} tabularNums`}
                    min={0}
                    step="0.01"
                    value={freightAmount}
                    onChange={(e) => setFreightAmount(e.target.value)}
                    disabled={!canApprove}
                  />
                </label>
                {/* Editable-terms task: admin-screen overrides on OUR
                    document terms — labelled "Our ..." throughout, same
                    distinct-from-the-buyer's-ask convention
                    WorkspacePage.jsx uses, including the same buyer-asked-
                    for contrast note placed right beside the delivery
                    field rather than left only in the collapsed "more
                    details" section above. */}
                <label className={styles.approveField}>
                  {t('approvalDetail.actions.deliveryDaysLabel')}
                  <input
                    type="number"
                    className={`${styles.approveFieldInput} tabularNums`}
                    min={0}
                    max={365}
                    step="1"
                    value={approveDeliveryDays}
                    onChange={(e) => setApproveDeliveryDays(e.target.value)}
                    disabled={!canApprove}
                  />
                  {quotation.request_needed_by && (
                    <span className={styles.approveFieldBuyerAskedNote}>
                      {t('approvalDetail.actions.buyerAskedForNote', {
                        date:
                          formatDate(quotation.request_needed_by, lang) ||
                          quotation.request_needed_by,
                      })}
                    </span>
                  )}
                </label>
                <label className={styles.approveField}>
                  {t('approvalDetail.actions.paymentTermsLabel')}
                  <input
                    type="text"
                    className={`${styles.approveFieldInput} ${styles.approveFieldInputWide}`}
                    maxLength={255}
                    value={approvePaymentTerms}
                    onChange={(e) => setApprovePaymentTerms(e.target.value)}
                    disabled={!canApprove}
                  />
                </label>
                <label className={styles.approveField}>
                  {t('approvalDetail.actions.validUntilLabel')}
                  <input
                    type="date"
                    className={`${styles.approveFieldInput} tabularNums`}
                    value={approveValidUntil}
                    onChange={(e) => setApproveValidUntil(e.target.value)}
                    disabled={!canApprove}
                  />
                </label>
              </div>
              <div className={styles.primaryActions}>
                <button
                  type="button"
                  className={styles.rejectButton}
                  onClick={() => setRejecting(true)}
                >
                  {t('approvalDetail.actions.reject')}
                </button>
                <button
                  type="button"
                  className={styles.approveButton}
                  onClick={handleApprove}
                  disabled={!canApprove || approveMutation.isPending}
                  title={!canApprove ? t('approvalDetail.actions.approveDisabledHint') : undefined}
                >
                  {approveMutation.isPending && <Spinner />}{' '}
                  {approveMutation.isPending
                    ? t('approvalDetail.actions.approving')
                    : t('approvalDetail.actions.approve')}
                </button>
              </div>
            </>
          ) : (
            <div className={styles.rejectForm}>
              <label className={styles.rejectLabel}>
                {t('approvalDetail.actions.rejectReasonLabel')}
                <textarea
                  className={styles.rejectTextarea}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder={t('approvalDetail.actions.rejectReasonPlaceholder')}
                  rows={2}
                />
              </label>
              <div className={styles.rejectFormActions}>
                <button
                  type="button"
                  className={styles.rejectCancelButton}
                  onClick={() => {
                    setRejecting(false);
                    setRejectReason('');
                  }}
                >
                  {t('approvalDetail.actions.rejectCancel')}
                </button>
                <button
                  type="button"
                  className={styles.rejectButton}
                  onClick={handleRejectConfirm}
                  disabled={rejectMutation.isPending}
                >
                  {rejectMutation.isPending && <Spinner />}{' '}
                  {rejectMutation.isPending
                    ? t('approvalDetail.actions.rejecting')
                    : t('approvalDetail.actions.rejectConfirm')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
